import { LoadingSkeleton } from "@components/skeleton-loading";
import { IRaffleDefaultTicketTransactionDetailDTO } from "@hooks/dtos/raffles/IRaffleDefaultTicketTransactionDTO";
import { Masks } from "@hooks/functions/Masks/Masks";
import { getRaffleDefaultTicketTransactionStatus } from "@hooks/functions/Order/status";
import { getRaffleStatus } from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { ReactNode, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowUpRightFromSquare,
  FaCalendarDays,
  FaCircleInfo,
  FaHashtag,
  FaReceipt,
  FaTicket,
  FaTrophy,
} from "react-icons/fa6";
import { IPMMercadoPRDTPrefDTO } from "@hooks/dtos/IPMMercadoPRDTPrefDTO";
import { PMMercadoPRDTPrefStatusEnum } from "@hooks/enums/pm_mercadop_rdt_pref";
import { IRaffleDefaultDTO } from "@hooks/dtos/raffles/IRaffleDefaultDTO";

interface IProps {
  orderId: number;
  isOpen: boolean;
  onClose: () => void;
}

const masks = new Masks();

export function ModalTicketOrderInfo({ orderId, isOpen, onClose }: IProps) {
  const navigate = useNavigate();
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  const [animate, setAnimate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [order, setOrder] =
    useState<IRaffleDefaultTicketTransactionDetailDTO>();
  const [payment, setPayment] = useState<IPMMercadoPRDTPrefDTO>();
  const [reorder, setReorder] = useState<{
    raffle: IRaffleDefaultDTO;
    tickets: string[];
  }>();

  async function handleLoadOrder() {
    setIsLoading(true);
    setOrder(undefined);

    try {
      const result = await api.get(`/raffleClient/default/order`, {
        params: { orderId },
      });
      setOrder(result?.data?.transaction);
      setPayment(result?.data?.payment);
      setReorder(result?.data?.reorder);
    } catch (err: any) {
      pushToastify({
        message:
          err?.response?.data.message ??
          "Ocorreu um erro ao carregar os dados!",
        type: "error",
        theme: "dark",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleCancel() {
    setAnimate(false);
    setTimeout(() => onClose(), 300);
  }

  const reorderCheckout = () => {
    navigate(`/raffle/checkout`, {
      state: {
        raffle: reorder?.raffle,
        raffleKey: reorder?.raffle?.key,
        campaign: reorder?.raffle?.campaign,
        selectedTickets: reorder?.tickets || [],
      },
    });
  };

  useEffect(() => {
    function handleEscape(evt: KeyboardEvent) {
      if (evt.key === "Escape" || evt.key === "Esc") handleCancel();
    }

    if (isOpen) {
      handleLoadOrder();
      setTimeout(() => setAnimate(true), 10);
      document.addEventListener("keydown", handleEscape);
    } else {
      setAnimate(false);
    }

    return () => document.removeEventListener("keydown", handleEscape);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, orderId]);

  const transactionStatus = order
    ? getRaffleDefaultTicketTransactionStatus(order.status)
    : undefined;

  const raffleStatus = order?.raffleData?.status
    ? getRaffleStatus(order.raffleData.status)
    : undefined;

  const tickets =
    order?.ordersData
      ?.map((item) => item.ticketData)
      .filter((ticket): ticket is NonNullable<typeof ticket> =>
        Boolean(ticket),
      ) || [];

  return (
    <>
      <div
        className={`modal fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        aria-labelledby="ticketOrderInfoLabel"
        aria-hidden={!isOpen}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content border-0 shadow">
            <div className="modal-header">
              <div>
                <h5 className="modal-title fw-bold" id="ticketOrderInfoLabel">
                  Detalhes da aquisição
                </h5>
                <small className="text-muted">
                  {order ? `Transação #${order.id}` : "Resumo da transação"}
                </small>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleCancel}
                aria-label="Fechar"
              />
            </div>

            <div className="modal-body bg-light">
              {isLoading ? (
                <LoadingSkeleton count={5} height={55} />
              ) : !order ? (
                <div className="text-center py-5">
                  <FaCircleInfo className="fs-2 text-muted mb-3" />
                  <h6 className="fw-bold">Aquisição não encontrada</h6>
                  <p className="text-muted mb-0">
                    Não foi possível apresentar os dados desta transação.
                  </p>
                </div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  <section className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <small className="text-muted fw-semibold">
                              STATUS DA CAMPANHA
                            </small>
                            {raffleStatus && (
                              <span
                                className={`badge text-bg-${raffleStatus.color}`}
                              >
                                {raffleStatus.text}
                              </span>
                            )}
                          </div>
                          <h5 className="fw-bold mb-1">
                            {order.raffleData?.name || "Campanha não informada"}
                          </h5>
                          <p className="small text-muted mb-2">
                            {order.raffleData?.description_short}
                          </p>
                          <div className="d-flex flex-wrap align-items-center gap-2">
                            <span className="small text-muted">
                              <FaHashtag className="me-1" />
                              {order.raffleData?.campaign || "-"}
                              {order.raffleData?.key &&
                                ` / ${order.raffleData.key}`}
                              {order.raffleData?.quantity &&
                                ` • ${masks.formatMilhar(
                                  order.raffleData.quantity,
                                )} números`}
                            </span>
                            {order.raffleData?.campaign && (
                              <Link
                                to={`/campaign/default?campaign=${order.raffleData.campaign}${
                                  order.raffleData.key
                                    ? `&key=${order.raffleData.key}`
                                    : ""
                                }`}
                                className="btn btn-sm btn-outline-primary py-1"
                                onClick={handleCancel}
                              >
                                Ver campanha
                                <FaArrowUpRightFromSquare className="ms-2" />
                              </Link>
                            )}
                          </div>
                        </div>

                        <div className="flex-shrink-0 border rounded bg-light px-3 py-2 text-md-end">
                          <small className="text-muted d-block fw-semibold">
                            STATUS DA TRANSAÇÃO
                          </small>
                          {transactionStatus && (
                            <span
                              className={`badge text-bg-${transactionStatus.color} mb-2`}
                            >
                              {transactionStatus.text}
                            </span>
                          )}
                          <small className="text-muted d-block">
                            Total da aquisição
                          </small>
                          <span className="fs-4 fw-bold text-primary">
                            {masks.brlCurrency(Number(order.value))}
                          </span>
                          <small className="text-muted d-block">
                            {tickets.length} ticket
                            {tickets.length === 1 ? "" : "s"}
                          </small>
                        </div>
                      </div>
                    </div>
                  </section>

                  <div className="row g-3">
                    <div className="col-lg-8">
                      <section className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3">
                          <div>
                            <h6 className="fw-bold mb-0">
                              <FaTicket className="text-primary me-2" />
                              Seus tickets
                            </h6>
                            <small className="text-muted">
                              Números vinculados a esta aquisição
                            </small>
                          </div>
                          <span className="badge rounded-pill text-bg-primary">
                            {tickets.length}
                          </span>
                        </div>
                        <div className="card-body">
                          {tickets.length ? (
                            <div className="d-flex flex-wrap gap-2">
                              {tickets.map((ticket) => (
                                <div
                                  className="border rounded bg-light px-3 py-2"
                                  key={`${ticket.order}-${ticket.ticket}`}
                                  title={`Número válido para sorteio: #${ticket.order}`}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <FaTicket className="text-primary" />
                                    <span className="text-muetd">
                                      #{ticket.order}
                                    </span>
                                  </div>
                                  <small className="fw-bold text-primary d-block mt-1">
                                    {ticket.ticket}
                                  </small>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-muted small py-3 text-center">
                              Nenhum ticket vinculado a esta aquisição.
                            </div>
                          )}
                        </div>
                      </section>
                    </div>

                    <div className="col-lg-4">
                      <section className="card border-0 shadow-sm h-100">
                        <div className="card-header bg-white py-3">
                          <h6 className="fw-bold mb-0">
                            <FaReceipt className="text-primary me-2" />
                            Resumo
                          </h6>
                        </div>
                        <div className="card-body p-0">
                          <InfoRow
                            icon={<FaTicket />}
                            label="Valor por ticket"
                            value={masks.brlCurrency(
                              Number(order.raffleData?.price || 0),
                            )}
                          />
                          <InfoRow
                            icon={<FaTrophy />}
                            label="Total em prêmios"
                            value={masks.brlCurrency(
                              Number(order.raffleData?.rewardPriceMarket || 0),
                            )}
                          />
                          <InfoRow
                            icon={<FaTicket />}
                            label="Quantidade"
                            value={`${tickets.length} ticket${
                              tickets.length === 1 ? "" : "s"
                            }`}
                            last
                          />
                        </div>
                      </section>
                    </div>
                  </div>

                  <section className="card border-0 shadow-sm">
                    <div className="card-body py-3">
                      <div className="row g-3">
                        <DateInfo label="Criada em" value={order.createdAt} />
                        <DateInfo label="Expira em" value={order.expireAt} />
                        <DateInfo
                          label="Pagamento"
                          value={order.paidAt}
                          empty="Aguardando pagamento"
                        />
                      </div>
                    </div>
                  </section>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {reorder?.tickets && reorder?.tickets?.length > 0 && (
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={() => {
                    reorderCheckout();
                  }}
                >
                  Finalizar Compra
                </button>
              )}
              {payment?.status === PMMercadoPRDTPrefStatusEnum.WAITING && (
                <button
                  type="button"
                  className="btn btn-success px-4"
                  onClick={() => {
                    window.open(
                      payment?.paymentUrl || payment?.sandboxUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  Efetuar Pagamento
                </button>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary px-4"
                onClick={handleCancel}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal-backdrop fade ${animate && isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
      />
    </>
  );
}

interface IInfoRowProps {
  icon: ReactNode;
  label: string;
  value: string;
  last?: boolean;
}

function InfoRow({ icon, label, value, last = false }: IInfoRowProps) {
  return (
    <div
      className={`d-flex align-items-center gap-3 px-3 py-3 ${
        last ? "" : "border-bottom"
      }`}
    >
      <span className="text-primary">{icon}</span>
      <div>
        <small className="text-muted d-block">{label}</small>
        <span className="fw-semibold">{value}</span>
      </div>
    </div>
  );
}

interface IDateInfoProps {
  label: string;
  value?: string | null;
  empty?: string;
}

function DateInfo({ label, value, empty = "-" }: IDateInfoProps) {
  return (
    <div className="col-md-4 d-flex gap-2">
      <FaCalendarDays className="text-primary mt-1 flex-shrink-0" />
      <div>
        <small className="text-muted d-block">{label}</small>
        <span className="small fw-semibold">
          {value ? dayjs(value).format("DD/MM/YYYY [às] HH:mm") : empty}
        </span>
      </div>
    </div>
  );
}
